import React from "react";
import { Row, Col, Divider, Progress, Card } from "antd";
import Chart from "react-apexcharts";
import { formatIndian } from "../../../Utils/utils";
import { Document } from "react-iconly";

const colors = {
  primary: "#c13c59",
  primaryDark: "#992038",
  primaryLight: "#e04e65",
  secondary: "#b9324c",
  secondaryDark: "#7d1c2f",
  secondaryLight: "#d6455c",
  accent: "#ad2042",
  gradient: "linear-gradient(135deg,rgb(163, 98, 112) 0%, #b9324c 100%)",
};

export default function SalesInvoiceCard({
  topCustomers = [],
  agingData = [],
}) {
  const agingCategories = agingData
    .filter(
      (item) => item.name !== "MTD" && item.name !== "YTD" && item.name !== "OS"
    )
    .map((item) => item.name);

  const agingValues = agingData
    .filter(
      (item) => item.name !== "MTD" && item.name !== "YTD" && item.name !== "OS"
    )
    .map((item) => item.value);

  const totalOutstanding = agingValues.reduce((sum, val) => sum + val, 0);

  const agingOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      background: "transparent",
      sparkline: { enabled: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: [colors.primary],
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "dark",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [colors.secondary],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.2,
        stops: [0, 90, 100],
      },
    },
    markers: {
      size: 6,
      colors: ["#fff"],
      strokeColors: colors.primary,
      strokeWidth: 2,
      hover: { size: 8 },
    },
    dataLabels: {
      enabled: true,
      style: {
        colors: [colors.primary],
        fontWeight: 600,
        fontSize: "12px",
      },
      formatter: formatIndian,
      offsetY: -10,
      background: {
        enabled: true,
        foreColor: "#fff",
        padding: 4,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "#E2E8F0",
        opacity: 0.9,
        dropShadow: {
          enabled: false,
          top: 1,
          left: 1,
          blur: 1,
          color: "#000",
          opacity: 0.45,
        },
      },
    },
    tooltip: {
      y: {
        formatter: formatIndian,
      },
      style: {
        fontSize: "12px",
      },
      theme: "dark",
      marker: {
        show: true,
      },
      x: {
        show: true,
        formatter: (val) => `Aging: ${val}`,
      },
    },
    xaxis: {
      categories: agingCategories,
      labels: {
        style: {
          colors: "#64748B",
          fontWeight: 500,
          fontSize: "12px",
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    colors: [colors.primary],
    grid: {
      show: true,
      borderColor: "#E2E8F0",
      strokeDashArray: 3,
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#64748B",
          fontWeight: 500,
          fontSize: "12px",
        },
        formatter: formatIndian,
      },
    },
    states: {
      hover: {
        filter: {
          type: "lighten",
          value: 0.15,
        },
      },
      active: {
        filter: {
          type: "darken",
          value: 0.35,
        },
      },
    },
  };

  const agingSeries = [
    {
      name: "Outstanding",
      data: agingValues,
    },
  ];

  const totalOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      sparkline: { enabled: false },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "70%",
        borderRadius: 6,
        distributed: true,
        dataLabels: {
          position: "center",
        },
      },
    },
    dataLabels: {
      enabled: true,
      position: "center",
      style: {
        colors: ["#fff"],
        fontWeight: 600,
        fontSize: "12px",
      },
      formatter: formatIndian,
      offsetX: 0,
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        color: "#000",
        opacity: 0.45,
      },
    },
    tooltip: {
      y: {
        formatter: formatIndian,
      },
      style: {
        fontSize: "12px",
      },
      theme: "dark",
    },
    xaxis: {
      categories: ["MTD", "YTD", "OS"],
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    colors: [colors.primary, colors.secondary, colors.accent],
    grid: {
      show: false,
    },
    yaxis: {
      labels: {
        style: {
          colors: "#64748B",
          fontWeight: 500,
          fontSize: "12px",
        },
      },
    },
  };

  const totalSeries = [
    {
      name: "Amount",
      data: [
        agingData.find((d) => d.name === "MTD")?.value || 0,
        agingData.find((d) => d.name === "YTD")?.value || 0,
        agingData.find((d) => d.name === "OS")?.value || 0,
      ],
    },
  ];

  const cardStyle = {
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    height: "100%",
  };

  return (
    <>
      <Card style={cardStyle} bodyStyle={{padding:12}}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                background: colors.gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#FFFFFF",
                fontWeight: "600",
                fontSize: "16px",
                boxShadow: `0 4px 6px -1px rgba(193, 60, 89, 0.3), 0 2px 4px -1px rgba(193, 60, 89, 0.1)`,
              }}
            >
              <Document />
            </div>
            <div>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "20px",
                  color: "#1E293B",
                  lineHeight: "1.3",
                }}
              >
                Ageingwise Outstanding
              </div>
              <div
                style={{
                  fontWeight: "400",
                  fontSize: "14px",
                  color: "#64748B",
                }}
              >
                Customer Outstanding Summary & Analytics
              </div>
            </div>
          </div>
        </div>

        <Card style={cardStyle} bodyStyle={{padding:12}}>
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "120px",
              height: "120px",
              background: `radial-gradient(circle, rgba(185, 50, 76, 0.1) 0%, rgba(255, 255, 255, 0) 70%)`,
              zIndex: 0,
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "16px",
                  color: "#1E293B",
                  marginBottom: "4px",
                }}
              >
                Ageing Buckets Analysis
              </div>
              <div
                style={{
                  fontSize: "13px",
                  color: "#64748B",
                  fontWeight: "500",
                }}
              >
                Payment delay patterns over time
              </div>
            </div>
            <div
              style={{
                fontSize: "12px",
                color: colors.primaryDark,
                background: "#F8FAFC",
                padding: "6px 12px",
                borderRadius: "20px",
                fontWeight: "500",
                border: `1px solid ${colors.primaryLight}`,
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.02)",
              }}
            >
              Last 30 Days
            </div>
          </div>

          <Chart
            options={agingOptions}
            series={agingSeries}
            type="area"
            height={260}
          />
        </Card>

        <Row className="hp-my-24" gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card style={{ height: "100%" }}>
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "16px",
                  color: "#1E293B",
                }}
              >
                Total Sales Metrics
              </div>
              <Chart
                options={totalOptions}
                series={totalSeries}
                type="bar"
                height={220}
              />
              <div
                style={{
                  marginTop: "16px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                {["MTD", "YTD", "OS"].map((metric, index) => {
                  const value =
                    agingData.find((d) => d.name === metric)?.value || 0;
                  const metricColors = [
                    colors.primary,
                    colors.secondary,
                    colors.accent,
                  ];
                  return (
                    <div key={metric} style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748B",
                          fontWeight: "500",
                          marginBottom: "4px",
                        }}
                      >
                        {metric}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: metricColors[index],
                        }}
                      >
                        ₹{formatIndian(value)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card style={cardStyle} bodyStyle={{padding:12}}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "16px",
                      color: "#1E293B",
                      marginBottom: "4px",
                    }}
                  >
                    Top 5 Customers
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#64748B",
                      fontWeight: "500",
                    }}
                  >
                    By Outstanding Amount
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: colors.primaryDark,
                    background: "#F8FAFC",
                    padding: "6px 12px",
                    borderRadius: "20px",
                    fontWeight: "500",
                    border: `1px solid ${colors.primaryLight}`,
                  }}
                >
                  {topCustomers.length} Records
                </div>
              </div>

              {topCustomers.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "180px",
                    color: "#94A3B8",
                    fontSize: "14px",
                    background: "#F8FAFC",
                    borderRadius: "8px",
                    border: "1px dashed #E2E8F0",
                  }}
                >
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <div style={{ marginTop: "8px" }}>
                    No customer data available
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      marginTop: "4px",
                      color: "#64748B",
                    }}
                  >
                    Add customers to see analytics
                  </div>
                </div>
              ) : (
                <div style={{ overflow: "hidden" }}>
                  {topCustomers.map((item, idx) => {
                    const percentage =
                      totalOutstanding > 0
                        ? Math.round(
                            (item.TotalOutstanding / totalOutstanding) * 100
                          )
                        : 0;

                    return (
                      <React.Fragment key={item.CustomerName + idx}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 0",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                background:
                                  "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: colors.primary,
                                fontWeight: "600",
                                fontSize: "14px",
                                flexShrink: 0,
                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                              }}
                            >
                              {idx + 1}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontWeight: "500",
                                  color: "#1E293B",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  fontSize: "14px",
                                }}
                                title={item.CustomerName}
                              >
                                {item.CustomerName}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              fontWeight: "600",
                              color: colors.primary,
                              fontSize: "15px",
                              marginLeft: "8px",
                              flexShrink: 0,
                            }}
                          >
                            ₹{formatIndian(item.TotalOustanding || 0)}
                          </div>
                        </div>
                        {idx < topCustomers.length - 1 && (
                          <Divider
                            style={{
                              margin: 0,
                              borderColor: "#F1F5F0",
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Card>
    </>
  );
}
